import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Button, Spinner } from "react-bootstrap";
import BadgeWithTooltip from "./BadgeWithTooltip";
import useInfiniteScroll from "../../hooks/useInfiniteScroll";
import { fetchModalTemplates, clearModalTemplates } from "./slice";
import formatDate from "./utils/formatDate";
import "./duplicateTemplateModal.css";

const DuplicateTemplateModal = ({ show, onHide, onDuplicate, isLoading }) => {
  const dispatch = useDispatch();
  const {
    modalTemplates,
    modalTemplatesCount,
    modalTemplatesPage,
    fetchModalTemplatesStatus,
  } = useSelector((state) => state.templates);

  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const prevShowRef = useRef(false);
  const fetchPromiseRef = useRef(null);

  // Fetch page 1 on open, abort and clear on close
  useEffect(() => {
    const justOpened = show && !prevShowRef.current;
    prevShowRef.current = show;

    if (justOpened) {
      fetchPromiseRef.current = dispatch(fetchModalTemplates(1));
    }
    if (!show) {
      if (fetchPromiseRef.current) {
        fetchPromiseRef.current.abort();
        fetchPromiseRef.current = null;
      }
      setSelectedTemplateId(null);
      dispatch(clearModalTemplates());
    }

    return () => {
      if (fetchPromiseRef.current) {
        fetchPromiseRef.current.abort();
        fetchPromiseRef.current = null;
      }
    };
  }, [show, dispatch]);

  // Infinite scroll via IntersectionObserver on the sentinel element
  const hasMore = modalTemplates.length < modalTemplatesCount;
  const isLoadingMore =
    fetchModalTemplatesStatus === "loading" && modalTemplatesPage > 0;

  const handleLoadMore = useCallback(() => {
    fetchPromiseRef.current = dispatch(
      fetchModalTemplates(modalTemplatesPage + 1)
    );
  }, [dispatch, modalTemplatesPage]);

  const { sentinelRef } = useInfiniteScroll({
    hasMore,
    isLoading: isLoadingMore,
    onLoadMore: handleLoadMore,
  });

  // Pre-select the default template when first page loads
  useEffect(() => {
    if (
      fetchModalTemplatesStatus === "success" &&
      modalTemplatesPage === 1 &&
      modalTemplates.length > 0 &&
      selectedTemplateId === null
    ) {
      const defaultTemplate = modalTemplates.find((t) => t.is_user_default);
      setSelectedTemplateId(
        defaultTemplate ? defaultTemplate.id : modalTemplates[0].id
      );
    }
  }, [
    fetchModalTemplatesStatus,
    modalTemplatesPage,
    modalTemplates,
    selectedTemplateId,
  ]);

  const handleDuplicate = () => {
    if (selectedTemplateId) {
      onDuplicate(selectedTemplateId);
    }
  };

  const handleRetry = () => {
    fetchPromiseRef.current = dispatch(fetchModalTemplates(1));
  };

  const isInitialLoading =
    fetchModalTemplatesStatus === "loading" && modalTemplatesPage === 0;
  const hasFetchError = fetchModalTemplatesStatus === "failed";

  return (
    <Modal
      show={show}
      onHide={isLoading ? undefined : onHide}
      centered
      backdrop={isLoading ? "static" : true}
      keyboard={!isLoading}
      className="duplicate-template-modal-wrapper"
    >
      <Modal.Body className="pt-0 pb-2">
        <Modal.Header className="modal-title-with-border mb-0" closeButton>
          <Modal.Title>Select a section template</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div className="duplicate-template-scrollable">
            {isInitialLoading ? (
              <div className="d-flex justify-content-center py-5">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            ) : hasFetchError && modalTemplates.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-danger mb-2">Failed to load templates.</p>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleRetry}
                >
                  Retry
                </Button>
              </div>
            ) : (
              <>
                {modalTemplates.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    className={`duplicate-template-card${
                      selectedTemplateId === template.id ? " selected" : ""
                    }`}
                    onClick={() => setSelectedTemplateId(template.id)}
                  >
                    <div className="duplicate-template-card-content">
                      <div className="duplicate-template-info">
                        <div className="duplicate-template-name">
                          {template.name}
                        </div>
                        <div className="template-badges">
                          {template.is_user_default && (
                            <BadgeWithTooltip
                              badgeType="default"
                              badgeProps={{ bg: "primary" }}
                            >
                              Default
                            </BadgeWithTooltip>
                          )}
                          {template.is_system_default && (
                            <BadgeWithTooltip
                              badgeType="systemBuiltIn"
                              badgeProps={{ bg: "secondary" }}
                            >
                              System Built-in
                            </BadgeWithTooltip>
                          )}
                        </div>
                        {!template.is_system_default && template.updated_at && (
                          <div className="duplicate-template-date">
                            Last modified: {formatDate(template.updated_at)}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
                {hasMore && (
                  <div
                    ref={sentinelRef}
                    className="d-flex justify-content-center pt-2 pb-1"
                  >
                    {isLoadingMore && (
                      <Spinner
                        animation="border"
                        size="sm"
                        role="status"
                      >
                        <span className="visually-hidden">
                          Loading more...
                        </span>
                      </Spinner>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="mt-0">
          <Button
            variant="outline-secondary"
            onClick={onHide}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleDuplicate}
            disabled={isLoading || !selectedTemplateId}
          >
            {isLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Duplicating...
              </>
            ) : (
              "Duplicate"
            )}
          </Button>
        </Modal.Footer>
      </Modal.Body>
    </Modal>
  );
};

export default DuplicateTemplateModal;
